import theme from '../../styles/theme';
import { ImageOptimized, ImageOptimizedProps } from '../image/ImageOptimized';
import Box from '@mui/material/Box';
import { CSSProperties, useEffect } from 'react';
import YouTube from '@u-wave/react-youtube';
import { LiveBadge } from '../LiveBadge';
import { useRouter } from 'next/router';
import { ReactGlider } from '../ReactGlider';
import { useMobile } from 'src/hooks/useMobile';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import IconButton from '@mui/material/IconButton';
import { useMediaControls } from 'src/hooks/useMediaControls';
import { TwitchPlayerBluMint } from './TwitchPlayerBluMint';
import { useMedia } from 'src/contexts/MediaContext';

export type GameMediaBannerProps = {
  gamePlayOptions: { isGamePlaying: boolean; firstGamePlay: boolean };
  unityOptions: { unityUrl: string; unityEnabled: boolean };
} & Pick<ImageOptimizedProps, 'root'>;

const CSS_INSIDE_BANNER: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
};

export function GameMediaBanner({
  unityOptions,
  gamePlayOptions,
}: GameMediaBannerProps) {
  const { setActiveMediaIndex, activeMediaIndex, media } = useMedia();
  const {
    activeMedia,
    onVideoEnd,
    pauseAutoplay,
    setPauseAutoplay,
    isVisible,
    visibleRef,
    intersectionRef,
    gliderRef,
  } = useMediaControls(activeMediaIndex);

  const { isGamePlaying, firstGamePlay } = gamePlayOptions;
  const router = useRouter();
  const isMobile = useMobile();
  useEffect(() => {
    if (router?.isReady && (activeMediaIndex || activeMediaIndex === 0)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            media: media[Number(activeMediaIndex)].src,
          },
        },
        undefined,
        { shallow: true },
      );
    }
    //eslint-disable-next-line
  }, [activeMediaIndex,router?.isReady]);

  return (
    <Box display={'flex'} flexDirection={'column'}>
      <Box
        ref={visibleRef}
        sx={{
          position: 'relative',
          display: 'inline-block',
          height: '100%',
          '&::after': {
            paddingTop: '56.25%',
            display: 'block',
            content: '""',
          },
          background: theme.palette.background.elevationSolid[2],
        }}
      >
        {unityOptions.unityEnabled && (isGamePlaying || firstGamePlay) && (
          <Box
            sx={{
              display: isGamePlaying ? 'flex' : 'none',
              visiblity: isGamePlaying ? 'visible' : 'hidden',
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              background: theme.palette.background.elevationSolid[2],
              ...CSS_INSIDE_BANNER,
            }}
          >
            <iframe
              className="play-game-iframe"
              data-autoPlay={isGamePlaying}
              data-mozallowfullscreen="true"
              allow="autoplay; fullscreen *; geolocation; microphone; camera; midi; monetization; xr-spatial-tracking; gamepad; gyroscope; accelerometer; xr; cross-origin-isolated"
              src={
                unityOptions?.unityUrl ||
                'https://v6p9d9t4.ssl.hwcdn.net/html/6491941/build/index.html'
              }
              allowFullScreen
              id="game_drop"
              style={{
                width: '100%',
                height: '100%',
                display: isGamePlaying ? 'flex' : 'none',
                visibility: isGamePlaying ? 'visible' : 'hidden',
              }}
            />
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                color: '#ffff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.2)',
                },
              }}
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const iframe: any = document.getElementById(
                  'game_drop',
                ) as HTMLIFrameElement;
                if (iframe) {
                  if (iframe.requestFullscreen) {
                    iframe.requestFullscreen();
                  } else if (iframe.mozRequestFullScreen) {
                    iframe.mozRequestFullScreen();
                  } else if (iframe.webkitRequestFullscreen) {
                    iframe.webkitRequestFullscreen();
                  } else if (iframe.msRequestFullscreen) {
                    iframe.msRequestFullscreen();
                  }
                }
              }}
            >
              <FullscreenIcon />
            </IconButton>
          </Box>
        )}
        {activeMedia && !isGamePlaying && (
          <div>
            <TwitchPlayerBluMint activeMediaUrl={activeMedia.src} />

            {
              <YouTube
                video={
                  activeMedia.type === 'video'
                    ? activeMedia.src
                        .replace('https://www.youtube.com/watch?v=', '')
                        .slice(0, 11)
                    : undefined
                }
                muted
                paused={!isVisible}
                autoplay
                playsInline
                showRelatedVideos={false}
                onEnd={() => {
                  onVideoEnd(pauseAutoplay);
                }}
                className={
                  activeMedia.type === 'video' ? 'visible' : 'invisible'
                }
                style={{
                  ...CSS_INSIDE_BANNER,
                  width: '100%',
                  height: '100%',
                }}
              />
            }

            {activeMedia.type === 'image' && (
              <>
                <ImageOptimized
                  src={activeMedia.src}
                  alt={''}
                  layout={'fill'}
                  objectFit={'fill'}
                />
              </>
            )}
          </div>
        )}
      </Box>
      <Box
        width={'100%'}
        sx={{
          background: theme.palette.background.elevationSolid[2],
        }}
        onMouseEnter={() => {
          setPauseAutoplay(true);
        }}
        onMouseLeave={() => {
          setPauseAutoplay(false);
        }}
        ref={intersectionRef}
      >
        <ReactGlider
          itemWidth={234}
          hasArrows={!isMobile}
          gliderRef={gliderRef}
        >
          {media.map((mediaData, index) => {
            return (
              <Box
                key={`slide-${mediaData.src}`}
                style={{
                  cursor: 'pointer',
                  ...(Math.abs(activeMediaIndex - index) <= 1
                    ? { background: theme.palette.primary.vertical }
                    : {}),
                }}
                onClick={() => {
                  setActiveMediaIndex(index);
                }}
              >
                <Box
                  height={'125px'}
                  width={'100%'}
                  position={'relative'}
                  sx={{
                    transform:
                      activeMediaIndex === index
                        ? 'scale(0.97, 0.95)'
                        : 'scale(1)',
                    transition: 'transform 100ms ease-in-out',
                  }}
                >
                  <ImageOptimized
                    src={
                      mediaData.type === 'image'
                        ? mediaData.src
                        : mediaData.thumbnailUrl
                    }
                    alt={''}
                    layout={'fill'}
                    height="100%"
                    objectFit={'fill'}
                    root={
                      intersectionRef.current?.querySelector(
                        '.glider-container',
                      ) || undefined
                    }
                  />
                  {mediaData.type === 'twitch' && (
                    <LiveBadge
                      sx={{
                        top: 8,
                        left: 8,
                        boxShadow: '0px 2px 5px 5px rgba(0, 0, 0, 0.3)',
                        zIndex: 1,
                        position: 'absolute',
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </ReactGlider>
      </Box>
    </Box>
  );
}
